import type { Schemas } from "@softmax/observatory-client/types";
import type { EpisodeRow } from "../types";

export type EpisodesViewer = {
  readonly playerId: string;
  readonly playerName: string;
};

export function mySeatIndex({
  policies,
  names,
  viewer,
}: {
  readonly policies: readonly Schemas["EpisodePolicySummary"][];
  readonly names: readonly string[] | undefined;
  readonly viewer: EpisodesViewer;
}): number | null {
  const byPlayerId = policies.findIndex(
    (policy) => policy.player_id === viewer.playerId,
  );

  if (byPlayerId !== -1) return byPlayerId;

  if (!names) return null;

  const byName = names.indexOf(viewer.playerName);
  return byName !== -1 ? byName : null;
}

export function opponentsOf({
  policies,
  viewer,
  seatIndex,
  won,
  scores,
}: {
  readonly policies: readonly Schemas["EpisodePolicySummary"][];
  readonly viewer: EpisodesViewer;
  readonly seatIndex: number;
  readonly won: readonly (boolean | null)[];
  readonly scores: readonly number[];
}): string[] {
  const others = policies.flatMap((policy, i) => {
    if (
      i === seatIndex ||
      policy.player_id === viewer.playerId ||
      policy.policy_name === null
    )
      return [];

    const label =
      policy.version === null
        ? policy.policy_name
        : `${policy.policy_name}:v${policy.version}`;
    return [{ i, label }];
  });
  const otherSide = others.filter(
    (other) => won[seatIndex] !== null && won[other.i] !== won[seatIndex],
  );
  return (otherSide.length > 0 ? otherSide : others)
    .toSorted((a, b) => (scores[b.i] ?? 0) - (scores[a.i] ?? 0))
    .map((other) => other.label);
}

export function outcomeOf({
  won,
  seatIndex,
}: {
  readonly won: readonly (boolean | null)[];
  readonly seatIndex: number;
}): EpisodeRow["outcome"] {
  if (won[seatIndex]) return "won";

  if (won[seatIndex] === false && won.includes(true)) return "lost";

  return won.includes(null) ? null : "draw";
}
